import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import StatCard from '../components/StatCard';
import ChartCard from '../components/ChartCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { PeriodConfig, formatDate, COLORS } from '../types';

interface SubscriptionsTabProps {
  period: PeriodConfig;
}

const ENDPOINTS = ['subscription-funnel', 'revenue-dashboard'];

const SubscriptionsTab: React.FC<SubscriptionsTabProps> = ({ period }) => {
  const { data, loading } = useAnalyticsData(ENDPOINTS, period);

  if (loading) return <LoadingSpinner />;

  const funnel = data['subscription-funnel'] || {};
  const tf = funnel.trialFunnel || {};
  const sl = funnel.subLifecycle || {};
  const ip = sl.initialPurchases || {};
  const renewals = sl.renewals || {};
  const cancellations = sl.cancellations || {};
  const expirations = sl.expirations || {};

  // Funnel bar chart data
  const funnelSteps = [
    { step: 'Signups (24h)', count: funnel.last24hSignups || 0 },
    { step: 'Shown Paywall', count: funnel.paywallGatedUsers || 0 },
    { step: 'Paywall Views', count: tf.viewUsers || 0 },
    { step: 'Checkout Start', count: tf.checkoutStartUsers || 0 },
    { step: 'Subscribed', count: tf.successUsers || 0 },
    { step: 'Timer Expired', count: tf.expireUsers || 0 },
  ];

  // Plan breakdown for pie chart
  const planData = Object.entries(tf.successByPlan || {}).map(([plan, count]) => ({
    name: plan === 'yearly_trial' ? '3-Day Free Trial (Yearly)' : plan === 'weekly' ? 'Weekly' : plan === 'yearly' ? 'Yearly Direct' : plan,
    value: count as number,
  }));

  // Cancellation reasons pie
  const cancelReasonData = Object.entries(cancellations.byReason || {}).map(([reason, count]) => ({
    name: reason, value: count as number,
  }));

  // Daily subscription lifecycle chart
  const dailySub = sl.daily || [];

  // Daily trial funnel chart
  const dailyFunnel = tf.dailyFunnel || [];

  return (
    <div className="space-y-6">
      {/* Top Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Paywall Gated Users" value={funnel.paywallGatedUsers || 0} subtitle="Total users shown trial" />
        <StatCard title="Subscribed" value={funnel.paywallGatedSubscribed || 0} subtitle={`${funnel.paywallGatedConversion || 0}% conversion`} />
        <StatCard title="Trial Paywall Views" value={tf.viewUsers || 0} subtitle={`${tf.conversionRate || 0}% convert`} />
        <StatCard title="Timer Expired" value={tf.expireUsers || 0} subtitle={`${tf.abandonRate || 0}% abandon rate`} />
        <StatCard title="Signups (24h)" value={funnel.last24hSignups || 0} subtitle="New registrations" />
      </div>

      {/* Onboarding -> Subscription Funnel */}
      <ChartCard title="Onboarding \u2192 Subscription Funnel">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={funnelSteps} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} />
            <YAxis type="category" dataKey="step" stroke="#64748b" fontSize={11} tickLine={false} width={120} />
            <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
            <Bar dataKey="count" fill="#FF9C15" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Plan Distribution */}
        <ChartCard title="Subscription Plan Chosen">
          {planData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={planData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}>
                  {planData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-center py-8">No subscription data yet</p>}
        </ChartCard>

        {/* Checkout by Plan */}
        <ChartCard title="Trial Paywall Details">
          <div className="space-y-3 p-2">
            <div className="flex justify-between text-sm"><span className="text-gray-400">Checkout Starts</span><span className="text-white font-semibold">{tf.checkoutStarts || 0}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Successes</span><span className="text-green-400 font-semibold">{tf.successes || 0}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Failures</span><span className="text-red-400 font-semibold">{tf.fails || 0}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Timer Expires</span><span className="text-yellow-400 font-semibold">{tf.expires || 0}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Plan Selects</span><span className="text-white font-semibold">{JSON.stringify(tf.planSelects || {})}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Follow Actions</span><span className="text-white font-semibold">{tf.followUsers || 0} users</span></div>
            <hr className="border-white/10" />
            <div className="flex justify-between text-sm"><span className="text-gray-400">Checkout by Plan</span></div>
            {Object.entries(tf.checkoutByPlan || {}).map(([plan, count]) => (
              <div key={plan} className="flex justify-between text-sm pl-4"><span className="text-gray-500">{plan}</span><span className="text-white">{count as number}</span></div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Daily Trial Funnel */}
      {dailyFunnel.length > 0 && (
        <ChartCard title="Daily Trial Paywall (Views / Subscribed / Expired)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyFunnel.map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              <Legend />
              <Bar dataKey="views" fill="#FF9C15" name="Views" />
              <Bar dataKey="successes" fill="#4ade80" name="Subscribed" />
              <Bar dataKey="expires" fill="#f87171" name="Expired" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Subscription Lifecycle (RevenueCat) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Purchases (30d)" value={ip.total || 0} subtitle={`${ip.trialStarts || 0} trials, ${ip.weeklyStarts || 0} weekly, ${ip.yearlyDirect || 0} yearly`} />
        <StatCard title="Renewals (30d)" value={renewals.total || 0} subtitle="Active subscribers renewing" />
        <StatCard title="Cancellations (30d)" value={cancellations.total || 0} subtitle="Turned off auto-renew" />
        <StatCard title="Expirations (30d)" value={expirations.total || 0} subtitle="Subscription ended" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cancellation Reasons */}
        <ChartCard title="Cancellation Reasons (30d)">
          {cancelReasonData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={cancelReasonData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}>
                  {cancelReasonData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-500 text-center py-8">No cancellation data yet</p>}
        </ChartCard>

        {/* Purchases by Plan (30d) */}
        <ChartCard title="Purchases by Plan (30d)">
          <div className="space-y-3 p-2">
            {Object.entries(ip.byPlan || {}).map(([plan, count]) => (
              <div key={plan} className="flex justify-between text-sm">
                <span className="text-gray-400">{plan}</span>
                <span className="text-white font-semibold">{count as number}</span>
              </div>
            ))}
            {Object.keys(ip.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No purchase data yet</p>}
          </div>
        </ChartCard>
      </div>

      {/* Daily Subscription Lifecycle */}
      {dailySub.length > 0 && (
        <ChartCard title="Daily Subscription Events (30d)">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailySub.map((d: any) => ({ ...d, date: formatDate(d.date) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
              <Legend />
              <Line type="monotone" dataKey="purchases" stroke="#FF9C15" strokeWidth={2} dot={false} name="Purchases" />
              <Line type="monotone" dataKey="renewals" stroke="#4ade80" strokeWidth={2} dot={false} name="Renewals" />
              <Line type="monotone" dataKey="cancellations" stroke="#f87171" strokeWidth={2} dot={false} name="Cancellations" />
              <Line type="monotone" dataKey="expirations" stroke="#fbbf24" strokeWidth={2} dot={false} name="Expirations" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Renewal by Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Renewals by Plan (30d)">
          <div className="space-y-3 p-2">
            {Object.entries(renewals.byPlan || {}).map(([plan, count]) => (
              <div key={plan} className="flex justify-between text-sm">
                <span className="text-gray-400">{plan}</span>
                <span className="text-green-400 font-semibold">{count as number}</span>
              </div>
            ))}
            {Object.keys(renewals.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No renewal data yet</p>}
          </div>
        </ChartCard>

        <ChartCard title="Cancellations by Plan (30d)">
          <div className="space-y-3 p-2">
            {Object.entries(cancellations.byPlan || {}).map(([plan, count]) => (
              <div key={plan} className="flex justify-between text-sm">
                <span className="text-gray-400">{plan}</span>
                <span className="text-red-400 font-semibold">{count as number}</span>
              </div>
            ))}
            {Object.keys(cancellations.byPlan || {}).length === 0 && <p className="text-gray-500 text-center py-4">No cancellation data yet</p>}
          </div>
        </ChartCard>
      </div>
    </div>
  );
};

export default SubscriptionsTab;
